import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShoppingBag, CheckCircle2, AlertCircle, Clock, FileText,
  CreditCard, AlertTriangle, RefreshCw, ArrowRight, Mail, X,
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid,
  XAxis, YAxis, Tooltip } from 'recharts';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import KpiCard from '../components/KpiCard';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatCurrency, formatDate, shortId } from '../lib/utils';
import StatusBadge from '../components/StatusBadge';
import { useEnvInfo } from '../hooks/useEnvInfo';

export default function DashboardPage() {
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const { isProduction } = useEnvInfo();

  const kpisQuery = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: async () => (await api.get('/dashboard/kpis')).data,
  });
  const activityQuery = useQuery({
    queryKey: ['dashboard', 'activity'],
    queryFn: async () => (await api.get('/dashboard/activity')).data,
  });
  const clearingSeries = useQuery({
    queryKey: ['dashboard', 'clearing-series'],
    queryFn: async () => (await api.get('/dashboard/clearing-series?days=14')).data,
  });

  const seedOrders = useMutation({
    mutationFn: async () =>
      (await api.post('/simulator/orders', { count: 25, repeat_customers: true })).data,
    onSuccess: (data) => {
      toast.success(`+${data.count} órdenes generadas`);
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: () => toast.error('Error generando órdenes'),
  });
  const seedSettlement = useMutation({
    mutationFn: async () =>
      (await api.post('/simulator/settlement', { include_uncategorized: false })).data,
    onSuccess: () => {
      toast.success('Liquidación generada');
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['settlements'] });
    },
    onError: () => toast.error('Error generando liquidación'),
  });

  const k = kpisQuery.data || {};
  const series = (clearingSeries.data?.series || []).map((s) => ({
    date: s.date.slice(5), balance: s.balance,
  }));

  return (
    <div data-testid="dashboard-page" className="animate-fade-slide-up">
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        actions={
          !isProduction ? (
            <>
              <Button
                variant="outline"
                size="default"
                onClick={() => seedOrders.mutate()}
                disabled={seedOrders.isPending}
                data-testid="dashboard-simulate-orders-button"
              >
                <RefreshCw className={`h-[14px] w-[14px] ${seedOrders.isPending ? 'animate-spin' : ''}`} />
                {t('dashboard.simulate_orders')}
              </Button>
              <Button
                size="default"
                onClick={() => seedSettlement.mutate()}
                disabled={seedSettlement.isPending}
                data-testid="dashboard-simulate-settlement-button"
              >
                {t('dashboard.simulate_settlement')}
              </Button>
            </>
          ) : null
        }
      />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-kpis">
        <KpiCard
          index={0}
          label={t('dashboard.orders_today')}
          value={k.orders_today_total ?? 0}
          icon={ShoppingBag}
          testid="kpi-orders-today"
        />
        <KpiCard
          index={1}
          label={t('dashboard.synced')}
          value={k.orders_today_synced ?? 0}
          icon={CheckCircle2}
          tone="success"
          testid="kpi-orders-synced"
        />
        <KpiCard
          index={2}
          label={t('dashboard.pending')}
          value={(k.orders_today_pending ?? 0) + (k.orders_today_manual ?? 0)}
          icon={Clock}
          tone="warning"
          testid="kpi-orders-pending"
        />
        <KpiCard
          index={3}
          label={t('dashboard.failed')}
          value={k.orders_today_failed ?? 0}
          icon={AlertCircle}
          tone="danger"
          testid="kpi-orders-failed"
        />
        <KpiCard
          index={4}
          label={t('dashboard.settlements_month')}
          value={k.settlements_month ?? 0}
          icon={FileText}
          testid="kpi-settlements-month"
        />
        <KpiCard
          index={5}
          label={t('dashboard.clearing_balance')}
          value={formatCurrency(k.clearing_balance ?? 0, locale)}
          icon={CreditCard}
          tone="accent"
          highlight
          testid="kpi-clearing-balance"
        />
        <KpiCard
          index={6}
          label={t('dashboard.aged_alerts')}
          value={k.aged_alerts ?? 0}
          icon={AlertTriangle}
          tone={(k.aged_alerts ?? 0) > 0 ? 'danger' : 'warning'}
          testid="kpi-aged-alerts"
        />
        <KpiCard
          index={7}
          label={t('dashboard.manual_review')}
          value={k.manual_review_pending ?? 0}
          icon={Clock}
          tone="info"
          testid="kpi-manual-review"
        />
      </div>

      {/* Chart + Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Chart card */}
        <div
          className="lg:col-span-2 premium-card animate-fade-slide-up"
          style={{ padding: '24px', animationDelay: '240ms' }}
          data-testid="clearing-chart-card"
        >
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-[15px] font-bold tracking-tight text-foreground leading-tight">
                {t('dashboard.clearing_health')}
              </h3>
              <p className="text-[12px] text-muted-foreground mt-0.5">14 días</p>
            </div>
            <span
              className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold"
              style={{
                padding: '4px 10px',
                background: 'var(--success-bg)',
                border: '1px solid var(--success-border)',
                color: 'var(--success-fg)',
              }}
            >
              <span
                className="h-[6px] w-[6px] rounded-full"
                style={{ background: 'var(--success-fg)', animation: 'pulseDot 2s ease infinite' }}
              />
              En vivo
            </span>
          </div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4A843" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#D4A843" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--chart-tooltip-bg))',
                    border: '1px solid var(--chart-tooltip-border)',
                    borderRadius: 10,
                    fontSize: 12,
                    padding: '10px 14px',
                    color: 'hsl(var(--foreground))',
                    boxShadow: 'var(--shadow-md)',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="#D4A843"
                  fill="url(#goldArea)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: '#D4A843',
                    stroke: 'rgba(212,168,67,0.30)',
                    strokeWidth: 8,
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent orders */}
        <div
          className="premium-card animate-fade-slide-up"
          style={{ padding: '20px 0', animationDelay: '300ms' }}
          data-testid="activity-feed-card"
        >
          <div className="flex items-center justify-between px-5 mb-1">
            <h3 className="text-[14px] font-bold tracking-tight text-foreground">
              {t('dashboard.activity_recent_orders')}
            </h3>
            <Link
              to="/orders"
              className="inline-flex items-center gap-1 text-[12px] font-medium text-[hsl(var(--primary))] hover:brightness-110 transition-all"
            >
              Ver todas <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <ScrollArea style={{ height: 220 }} className="mt-2">
            <div data-testid="recent-orders-list">
              {(activityQuery.data?.recent_orders || []).map((o) => (
                <Link
                  key={o.tiktok_order_id}
                  to="/orders"
                  className="group flex items-center justify-between px-5 py-3 border-b border-subtle last:border-0 hover:bg-foreground/[0.03] transition-colors duration-[120ms]"
                >
                  <div className="min-w-0 mr-3">
                    <div className="font-mono text-[12px] text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors truncate font-medium">
                      {shortId(o.tiktok_order_id, 10, 4)}
                    </div>
                    <div className="text-[13px] text-foreground font-medium truncate mt-0.5">
                      {o.raw?.customer?.full_name || 'Cliente'}
                    </div>
                  </div>
                  <StatusBadge status={o.status} />
                </Link>
              ))}
              {(activityQuery.data?.recent_orders || []).length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-[12px] text-muted-foreground">
                  {t('common.empty')}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Settlements + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div
          className="premium-card animate-fade-slide-up"
          style={{ padding: '20px 0', animationDelay: '360ms' }}
        >
          <div className="flex items-center justify-between px-5 mb-1">
            <h3 className="text-[14px] font-bold tracking-tight text-foreground">
              {t('dashboard.activity_recent_settlements')}
            </h3>
            <Link
              to="/settlements"
              className="inline-flex items-center gap-1 text-[12px] font-medium text-[hsl(var(--primary))] hover:brightness-110 transition-all"
            >
              Ver todas <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div data-testid="recent-settlements-list" className="mt-2">
            {(activityQuery.data?.recent_settlements || []).map((s) => (
              <Link
                key={s.tiktok_settlement_id}
                to={`/settlements/${s.tiktok_settlement_id}`}
                className="group flex items-center justify-between px-5 py-3 border-b border-subtle last:border-0 hover:bg-foreground/[0.03] transition-colors duration-[120ms]"
              >
                <div className="min-w-0 mr-3">
                  <div className="font-mono text-[12px] text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors truncate font-medium">
                    {shortId(s.tiktok_settlement_id, 14, 4)}
                  </div>
                  <div className="text-[12px] text-muted-foreground/80 font-mono mt-0.5">
                    {formatDate(s.settlement_date, locale)}
                  </div>
                </div>
                <StatusBadge status={s.status || 'pending'} />
              </Link>
            ))}
            {(activityQuery.data?.recent_settlements || []).length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-[12px] text-muted-foreground">
                {t('common.empty')}
              </div>
            )}
          </div>
        </div>

        <div
          className="premium-card animate-fade-slide-up"
          style={{ padding: '20px 24px', animationDelay: '420ms' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-bold tracking-tight text-foreground">
              {t('dashboard.activity_recent_alerts')}
            </h3>
            {(activityQuery.data?.recent_alerts || []).length > 0 && (
              <span
                className="text-[10px] font-bold rounded-full"
                style={{
                  padding: '1px 6px',
                  background: 'var(--warning-bg)',
                  border: '1px solid var(--warning-border)',
                  color: 'var(--warning-fg)',
                }}
              >
                {(activityQuery.data?.recent_alerts || []).length}
              </span>
            )}
          </div>
          <div className="space-y-2" data-testid="recent-alerts-list">
            {(activityQuery.data?.recent_alerts || []).map((a, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 rounded-lg px-3.5 py-2.5"
                style={{
                  background: 'var(--border-subtle)',
                  border: '1px solid var(--border-default)',
                }}
              >
                <Mail className="h-[14px] w-[14px] text-muted-foreground/70 shrink-0" />
                <span className="text-[13px] text-muted-foreground flex-1 truncate">{a.type}</span>
                <button
                  type="button"
                  className="text-muted-foreground/60 hover:text-foreground transition-colors p-1"
                  aria-label="Dismiss"
                >
                  <X className="h-[12px] w-[12px]" />
                </button>
              </div>
            ))}
            {(activityQuery.data?.recent_alerts || []).length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-[12px] text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 mb-2" style={{ color: 'var(--success-fg)', opacity: 0.6 }} />
                Sin alertas activas
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
